import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Badge,
  Collapse,
  Flex,
  HStack,
  SimpleGrid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import JobReferralsApi from '../../../../api/jobReferrals';
import { useCompaniesOptions } from '../../../../hooks/useCompaniesOptions';
import User from '../../../../api/user';

const STATUS_LABELS = { open: 'פתוח', paused: 'מושהה', closed: 'סגור' };
const STATUS_COLORS = { open: 'green', paused: 'yellow', closed: 'red' };

const ELIGIBLE_MONTHS_OPTIONS = [
  { value: '', label: 'ללא' },
  { value: 1, label: 'חודש אחד' },
  { value: 2, label: 'חודשיים' },
  { value: 3, label: '3 חודשים' },
];

const EMPTY_FORM = {
  companyId: '',
  title: '',
  location: '',
  department: '',
  bonusAmount: '',
  rewardEligibleMonths: '',
  description: '',
  salary: '',
  shiftType: '',
  requirements: '',
};

const PAGE_SIZE = 50;

export default function JobsTab({ isSuperAdmin }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingJob, setEditingJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [inlineEdit, setInlineEdit] = useState({});
  const [inlineSaving, setInlineSaving] = useState(false);

  const toggleExpand = (job) => {
    setExpandedJobId((prev) => {
      if (prev === job.id) return null;
      setInlineEdit({
        bonusAmount: job.bonusAmount ?? '',
        rewardEligibleMonths: job.rewardEligibleMonths ?? '',
      });
      return job.id;
    });
  };

  const saveInline = async (job) => {
    setInlineSaving(true);
    try {
      await JobReferralsApi.updateJob(job.id, {
        bonusAmount: parseFloat(inlineEdit.bonusAmount) || 0,
        rewardEligibleMonths: inlineEdit.rewardEligibleMonths !== '' ? inlineEdit.rewardEligibleMonths : null,
      });
      toast({ title: 'עודכן', status: 'success', duration: 2000 });
      fetchJobs();
    } catch {
      toast({ title: 'שגיאה בשמירה', status: 'error', duration: 3000 });
    } finally {
      setInlineSaving(false);
    }
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const expandedBg = useColorModeValue('gray.50', 'gray.700');
  const { companiesOptions } = useCompaniesOptions({ showAll: true });

  const fetchJobs = async () => {
    setLoading(true);
    setPage(1);
    try {
      const filterCompanyId = isSuperAdmin && selectedCompany && selectedCompany !== 'ALL' ? selectedCompany : undefined;
      const res = await JobReferralsApi.getJobs(filterCompanyId);
      setJobs(res.data || []);
    } catch {
      toast({ title: 'שגיאה בטעינת משרות', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [selectedCompany]);

  const totalPages = Math.max(1, Math.ceil(jobs.length / PAGE_SIZE));
  const pagedJobs = jobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => {
    setEditingJob(null);
    setForm(EMPTY_FORM);
    onOpen();
  };

  const openEdit = (job) => {
    setEditingJob(job);
    setForm({
      companyId: job.companyId,
      title: job.title,
      location: job.location,
      department: job.department,
      bonusAmount: job.bonusAmount,
      rewardEligibleMonths: job.rewardEligibleMonths ?? '',
      description: job.description || '',
      salary: job.salary || '',
      shiftType: job.shiftType || '',
      requirements: job.requirements || '',
    });
    onOpen();
  };

  const handleSave = async () => {
    if (!form.title || !form.location || !form.bonusAmount) {
      toast({ title: 'יש למלא את כל השדות החובה', status: 'warning', duration: 3000 });
      return;
    }
    setSaving(true);
    try {
      if (editingJob) {
        await JobReferralsApi.updateJob(editingJob.id, {
          title: form.title,
          location: form.location,
          department: form.department,
          bonusAmount: parseFloat(form.bonusAmount),
          rewardEligibleMonths: form.rewardEligibleMonths !== '' ? form.rewardEligibleMonths : null,
          description: form.description || undefined,
          salary: form.salary || undefined,
          shiftType: form.shiftType || undefined,
          requirements: form.requirements || undefined,
        });
      } else {
        await JobReferralsApi.createJob({
          companyId: parseInt(form.companyId),
          title: form.title,
          location: form.location,
          department: form.department,
          bonusAmount: parseFloat(form.bonusAmount),
          rewardEligibleMonths: form.rewardEligibleMonths !== '' ? form.rewardEligibleMonths : null,
          description: form.description || undefined,
          salary: form.salary || undefined,
          shiftType: form.shiftType || undefined,
          requirements: form.requirements || undefined,
        });
      }
      toast({ title: editingJob ? 'משרה עודכנה' : 'משרה נוצרה', status: 'success', duration: 2000 });
      onClose();
      fetchJobs();
    } catch {
      toast({ title: 'שגיאה בשמירה', status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (job) => {
    // Only toggle between open and paused — never auto-close a job,
    // since multiple candidates can be hired for the same position.
    const newStatus = job.status === 'open' ? 'paused' : 'open';
    try {
      await JobReferralsApi.updateJob(job.id, { status: newStatus });
      fetchJobs();
    } catch {
      toast({ title: 'שגיאה בשינוי סטטוס', status: 'error', duration: 3000 });
    }
  };

  return (
    <Card mt={4}>
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
        {isSuperAdmin && (
          <HStack>
            <Select
              placeholder="כל החברות"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              w="220px"
              dir="rtl"
            >
              {companiesOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </HStack>
        )}
        {isSuperAdmin && (
          <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>
            משרה חדשה
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" dir="rtl">
            <Thead>
              <Tr>
                <Th color={textColor} borderColor={borderColor}>כותרת</Th>
                <Th color={textColor} borderColor={borderColor}>מיקום</Th>
                <Th color={textColor} borderColor={borderColor}>מחלקה</Th>
                <Th color={textColor} borderColor={borderColor}>בונוס</Th>
                <Th color={textColor} borderColor={borderColor}>זכאות</Th>
                <Th color={textColor} borderColor={borderColor}>סטטוס</Th>
                <Th color={textColor} borderColor={borderColor}>חברה</Th>
                {isSuperAdmin && <Th color={textColor} borderColor={borderColor}>פעולות</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {jobs.length === 0 && (
                <Tr>
                  <Td colSpan={7} textAlign="center" color="gray.400">
                    אין משרות
                  </Td>
                </Tr>
              )}
              {pagedJobs.map((job) => {
                const isExpanded = expandedJobId === job.id;
                const colSpan = isSuperAdmin ? 8 : 7;
                const hasDetails = job.description || job.requirements || job.salary || job.shiftType || isSuperAdmin;
                return (
                  <React.Fragment key={job.id}>
                    <Tr
                      cursor="pointer"
                      _hover={{ bg: hoverBg }}
                      onClick={() => toggleExpand(job)}
                    >
                      <Td color={textColor} fontWeight="600">
                        <HStack spacing={2}>
                          {hasDetails && (
                            isExpanded
                              ? <ChevronUpIcon color="gray.400" />
                              : <ChevronDownIcon color="gray.400" />
                          )}
                          <Box>
                            {job.title}
                            {job.civiJobNum && (
                              <Badge colorScheme="blue" ms={2} fontSize="2xs" verticalAlign="middle">
                                Civi
                              </Badge>
                            )}
                          </Box>
                        </HStack>
                      </Td>
                      <Td color={textColor}>{job.location}</Td>
                      <Td color={textColor}>{job.department}</Td>
                      <Td color={textColor}>₪{Number(job.bonusAmount).toLocaleString()}</Td>
                      <Td color={textColor}>{ELIGIBLE_MONTHS_OPTIONS.find(o => o.value === job.rewardEligibleMonths)?.label ?? 'חודש אחד'}</Td>
                      <Td>
                        <Badge colorScheme={STATUS_COLORS[job.status] || 'gray'}>
                          {STATUS_LABELS[job.status] || job.status}
                        </Badge>
                      </Td>
                      <Td color={textColor}>{companiesOptions.find(c => Number(c.value) === job.companyId)?.label || User.GetInfo()?.companyName || job.companyId}</Td>
                      {isSuperAdmin && (
                        <Td onClick={(e) => e.stopPropagation()}>
                          {job.civiJobNum ? (
                            <Text fontSize="xs" color="gray.400">מנוהל ע״י Civi</Text>
                          ) : (
                            <HStack spacing={2}>
                              <Button size="xs" variant="outline" onClick={() => openEdit(job)}>
                                עריכה
                              </Button>
                              <Button
                                size="xs"
                                colorScheme={job.status === 'open' ? 'yellow' : 'green'}
                                variant="outline"
                                onClick={() => toggleStatus(job)}
                                isDisabled={job.status === 'closed'}
                              >
                                {job.status === 'open' ? 'השהה' : 'פתח'}
                              </Button>
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await JobReferralsApi.updateJob(job.id, { status: 'closed' });
                                    fetchJobs();
                                  } catch {
                                    toast({ title: 'שגיאה בסגירת משרה', status: 'error', duration: 3000 });
                                  }
                                }}
                                isDisabled={job.status === 'closed'}
                              >
                                סגור
                              </Button>
                            </HStack>
                          )}
                        </Td>
                      )}
                    </Tr>
                    {hasDetails && (
                      <Tr>
                        <Td colSpan={colSpan} p={0} borderBottom={isExpanded ? undefined : 'none'}>
                          <Collapse in={isExpanded} animateOpacity>
                            <Box px={6} py={4} bg={expandedBg} dir="rtl">
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                                {job.description && (
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={1}>תיאור המשרה</Text>
                                    <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap">{job.description}</Text>
                                  </Box>
                                )}
                                {job.requirements && (
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={1}>דרישות התפקיד</Text>
                                    <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap">{job.requirements}</Text>
                                  </Box>
                                )}
                                {job.salary && (
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={1}>שכר</Text>
                                    <Text fontSize="sm" color={textColor}>{job.salary}</Text>
                                  </Box>
                                )}
                                {job.shiftType && (
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={1}>סוג משמרת</Text>
                                    <Text fontSize="sm" color={textColor}>{job.shiftType}</Text>
                                  </Box>
                                )}
                              </SimpleGrid>
                              {isSuperAdmin && (
                                <HStack spacing={4} align="flex-end" onClick={(e) => e.stopPropagation()} flexWrap="wrap">
                                  <FormControl w="160px">
                                    <FormLabel fontSize="xs" fontWeight="700" color="gray.500" mb={1}>סכום בונוס (₪)</FormLabel>
                                    <NumberInput
                                      size="sm"
                                      value={inlineEdit.bonusAmount}
                                      onChange={(val) => setInlineEdit((s) => ({ ...s, bonusAmount: val }))}
                                      min={0}
                                    >
                                      <NumberInputField />
                                    </NumberInput>
                                  </FormControl>
                                  <FormControl w="180px">
                                    <FormLabel fontSize="xs" fontWeight="700" color="gray.500" mb={1}>תקופת זכאות לבונוס</FormLabel>
                                    <Select
                                      size="sm"
                                      value={inlineEdit.rewardEligibleMonths}
                                      onChange={(e) => setInlineEdit((s) => ({ ...s, rewardEligibleMonths: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                                    >
                                      {ELIGIBLE_MONTHS_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <Button size="sm" colorScheme="blue" isLoading={inlineSaving} onClick={() => saveInline(job)}>
                                    שמור
                                  </Button>
                                </HStack>
                              )}
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      {jobs.length > 0 && (
        <Flex justify="center" align="center" gap={4} mt={4}>
          <Button size="sm" onClick={() => setPage((p) => p - 1)} isDisabled={page === 1}>
            הקודם
          </Button>
          <Flex direction="column" align="center">
            <Text fontSize="sm">{page} / {totalPages}</Text>
            <Text fontSize="xs" color="gray.400">{jobs.length} סה"כ</Text>
          </Flex>
          <Button size="sm" onClick={() => setPage((p) => p + 1)} isDisabled={page >= totalPages}>
            הבא
          </Button>
        </Flex>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" dir="rtl">
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader>{editingJob ? 'עריכת משרה' : 'משרה חדשה'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={3}>
              {!editingJob && (
                <FormControl isRequired>
                  <FormLabel>חברה</FormLabel>
                  <Select
                    placeholder="בחר חברה"
                    value={form.companyId}
                    onChange={(e) => setForm({ ...form, companyId: e.target.value })}
                  >
                    {companiesOptions
                      .filter((c) => c.value !== 'ALL')
                      .map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                  </Select>
                </FormControl>
              )}

              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>שם משרה</FormLabel>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>מיקום המשרה</FormLabel>
                  <Input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>מחלקה</FormLabel>
                  <Input
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>שכר / שכר שעתי</FormLabel>
                  <Input
                    value={form.salary}
                    onChange={(e) => setForm({ ...form, salary: e.target.value })}
                    placeholder='לדוגמה: 45₪/שעה'
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>סוג משמרת</FormLabel>
                  <Input
                    value={form.shiftType}
                    onChange={(e) => setForm({ ...form, shiftType: e.target.value })}
                    placeholder='בוקר, ערב, לילה, גמיש'
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>סכום בונוס</FormLabel>
                  <NumberInput
                    value={form.bonusAmount}
                    onChange={(val) => setForm({ ...form, bonusAmount: val })}
                    min={0}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel>תקופת זכאות לבונוס</FormLabel>
                  <Select
                    value={form.rewardEligibleMonths}
                    onChange={(e) => setForm({ ...form, rewardEligibleMonths: e.target.value === '' ? '' : parseInt(e.target.value) })}
                  >
                    {ELIGIBLE_MONTHS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>תיאור המשרה</FormLabel>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                />
              </FormControl>
              <FormControl>
                <FormLabel>דרישות התפקיד</FormLabel>
                <Textarea
                  value={form.requirements}
                  onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                  rows={3}
                  placeholder='רשום את הדרישות לתפקיד...'
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button colorScheme="blue" isLoading={saving} onClick={handleSave}>
              שמור
            </Button>
            <Button variant="ghost" onClick={onClose}>
              ביטול
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}
